import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, ShieldCheck, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/6h7nm3ap_Photoroom_20260215_225825.png";

const Invite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { initiateGoogleLogin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await axios.get(`${API}/invite/${token}`);
        setInvite(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "This invite link is invalid or has expired");
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [token]);

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/invite/${token}/set-password`, { password }, { withCredentials: true });
      toast.success(`Welcome, ${res.data.name}! Your account is ready.`);
      navigate("/portal", { state: { user: res.data } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set up your account");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#39FF14] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div data-testid="invite-error" className="bg-[#121212] border border-red-500/30 rounded-2xl p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-white text-xl font-bold mb-2">Invite Unavailable</h1>
          <p className="text-[#A1A1AA] mb-6">{error}</p>
          <Button onClick={() => navigate("/")} className="bg-[#39FF14] text-black hover:bg-[#32d912]">Go to Homepage</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="Zig Zag Lawncare" className="h-16 w-auto object-contain mx-auto mb-4 rounded-lg" />
          <h1 className="text-white text-2xl font-bold font-heading uppercase">Welcome{invite?.name ? `, ${invite.name}` : ""}!</h1>
          <p className="text-[#A1A1AA] text-sm mt-2">
            Activate your Zig Zag Lawncare account to view your cut history and manage your service.
          </p>
        </div>

        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 mb-6 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#39FF14]" />
          <span className="text-white text-sm" data-testid="invite-email">{invite?.email}</span>
        </div>

        {/* Google */}
        <Button
          data-testid="invite-google-btn"
          onClick={() => initiateGoogleLogin("customer")}
          className="w-full bg-white text-black hover:bg-gray-100 font-semibold mb-4"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continue with Google
        </Button>

        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-[#27272A]" />
          <span className="text-[#52525B] text-xs uppercase">or set a password</span>
          <div className="flex-1 h-px bg-[#27272A]" />
        </div>

        {/* Password */}
        <form onSubmit={handleSetPassword} className="space-y-4">
          <div>
            <Label className="text-[#A1A1AA]">Create Password</Label>
            <div className="relative mt-1">
              <Lock className="w-4 h-4 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="invite-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="bg-[#0A0A0A] border-[#27272A] text-white pl-9 focus:border-[#39FF14]"
              />
            </div>
          </div>
          <div>
            <Label className="text-[#A1A1AA]">Confirm Password</Label>
            <div className="relative mt-1">
              <Lock className="w-4 h-4 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="invite-confirm-input"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter password"
                className="bg-[#0A0A0A] border-[#27272A] text-white pl-9 focus:border-[#39FF14]"
              />
            </div>
          </div>
          <Button
            data-testid="invite-submit-btn"
            type="submit"
            disabled={submitting}
            className="w-full bg-[#39FF14] text-black hover:bg-[#32d912] font-semibold"
          >
            {submitting ? "Setting up..." : "Activate My Account"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Invite;
